import { useQueries, UseQueryResult } from "@tanstack/react-query";
import { fetchPaginatedCollection } from "PFCore/helpers/collection";
import { ApiRoute } from "PFCore/utilities/routes";
import { useMemo } from "react";

import { RequestData } from "../get_request_data";
import { rowChildrenKeys } from "./query_keys";

export type RowChildrenItem = {
  entries: any[];
  meta: Record<string, any> & {
    page: number;
    total: number;
  };
};

interface UseRowChildren {
  requestData: RequestData[];
  type: string;
  mode: string;
  id: number;
  page: number;
  enabled: boolean;
  onSuccess: (data: RowChildrenItem, requestData: RequestData) => void;
}

interface UseRowChildrenReturn {
  childrenByType: Record<string, RowChildrenItem>;
  isLoading: boolean;
  isFetching: boolean;
}

const groupByChildrenType = (
  queries: UseQueryResult<RowChildrenItem>[],
  requestData: RequestData[]
): Record<string, RowChildrenItem> =>
  queries.reduce((result, { data }, index) => {
    if (!data) {
      return result;
    }
    const { childrenType } = requestData[index];
    if (!childrenType) {
      return result;
    }
    return {
      ...result,
      [childrenType]: data
    };
  }, {});

export const useRowChildren = ({
  requestData,
  type,
  mode,
  id,
  page,
  enabled,
  onSuccess
}: UseRowChildren): UseRowChildrenReturn => {
  const queriesConfig = useMemo(
    () =>
      requestData.map((request) => ({
        queryKey: rowChildrenKeys.ofRowPage(
          mode,
          type,
          id,
          request.childrenType,
          request.params.filters,
          request.unpaginated ? null : page
        ),
        queryFn: (): Promise<RowChildrenItem> =>
          fetchPaginatedCollection(
            ApiRoute(request.url),
            { ...request.params, page: request.unpaginated ? 1 : page },
            null,
            request.options
          ).then(request.mapResponse),
        keepPreviousData: true,
        enabled,
        onSuccess: (data) => onSuccess(data, request)
      })),
    [requestData, type, mode, id, page, enabled]
  );
  const queries = useQueries({
    queries: queriesConfig
  });
  const queriesDataStringified = JSON.stringify(queries.map(({ data }) => data));

  const isLoading = useMemo(() => enabled && !!queries.find(({ isLoading }) => isLoading), [queries]);
  const isFetching = useMemo(() => enabled && !!queries.find(({ isFetching }) => isFetching), [queries]);

  const childrenByType = useMemo<Record<string, RowChildrenItem>>(
    () => groupByChildrenType(queries, requestData),
    [requestData, queriesDataStringified]
  );

  return {
    childrenByType,
    isLoading,
    isFetching
  };
};
